import { API_URL } from "../config";
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Mail, KeyRound, Loader2, User as UserIcon, ArrowRight, CheckCircle2, Sparkles, Send, AlertCircle } from 'lucide-react';
import { LoginButton } from '@telegram-auth/react';
import { motion, AnimatePresence } from 'framer-motion';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [otp, setOtp] = useState('');
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const { login, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
        navigate('/dashboard');
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');
    try {
      if (isRegistering) {
          if (!showOtpInput) {
              const response = await axios.post(`${API_URL}/auth/register`, { name, email, password });
              setMessage(response.data.message);
              setShowOtpInput(true);
          } else {
              const response = await axios.post(`${API_URL}/auth/verify-register-otp`, { email, otp });
              login(response.data.token, response.data.user);
              navigate('/dashboard');
          }
      } else {
          const response = await axios.post(`${API_URL}/auth/login`, { email, password });
          login(response.data.token, response.data.user);
          navigate('/dashboard');
      }
    } catch (err) {
      console.error("Frontend error during auth:", err);
      if (!err.response) {
        setError('Network error: Could not reach the server. Is it running?');
      } else {
        setError(err.response?.data?.error || 'Authentication failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { id: 1, title: 'Create your account', active: isRegistering && !showOtpInput, done: showOtpInput || !isRegistering },
    { id: 2, title: 'Verify email via OTP', active: isRegistering && showOtpInput, done: !isRegistering },
    { id: 3, title: 'Unlock your workspace', active: !isRegistering, done: false }
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center p-4 sm:p-6 lg:p-8 font-sans selection:bg-purple-500/30">
      <div className="max-w-6xl w-full flex flex-col lg:flex-row bg-[#121212] rounded-3xl overflow-hidden border border-white/5 shadow-[0_0_40px_rgba(0,0,0,0.5)]">
        
        {}
        <div className="lg:w-5/12 p-2 hidden md:block">
          <div className="h-full w-full rounded-2xl relative overflow-hidden bg-black border border-white/10 flex flex-col justify-between p-10">
            {}
            <motion.div 
              animate={{ 
                background: [
                  "radial-gradient(circle at 0% 0%, #7e22ce 0%, transparent 50%)",
                  "radial-gradient(circle at 100% 100%, #7e22ce 0%, transparent 50%)",
                  "radial-gradient(circle at 0% 100%, #7e22ce 0%, transparent 50%)",
                  "radial-gradient(circle at 0% 0%, #7e22ce 0%, transparent 50%)"
                ]
              }}
              transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
              className="absolute inset-0 opacity-40 mix-blend-screen"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-purple-600/30 via-transparent to-black/80 z-0"></div>
            
            {}
            <div className="relative z-10 flex items-center gap-3">
              <div className="flex items-center gap-2 text-white">
                 <KeyRound className="w-6 h-6 -rotate-45 text-white drop-shadow-[0_0_8px_rgba(255,255,255,0.8)]" />
              </div>
              <span className="text-xl font-bold tracking-tight text-white">Linkey</span>
            </div>

            <div className="relative z-10 my-16">
              <motion.h1 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-4xl lg:text-5xl font-bold tracking-tight mb-4 bg-clip-text text-transparent bg-gradient-to-r from-white to-white/70"
              >
                {isRegistering ? 'Get Started with Us' : 'Welcome Back'}
              </motion.h1>
              <p className="text-gray-400 text-sm lg:text-base max-w-sm mb-12">
                {isRegistering 
                  ? 'Complete these easy steps to register your account and start creating gateways.'
                  : 'Sign in to access your dashboard, manage links, and track your analytics.'}
              </p>

              {}
              <div className="space-y-4">
                {steps.map((step) => (
                  <div 
                    key={step.id}
                    className={`flex items-center gap-4 p-4 rounded-2xl transition-all duration-500 border ${
                      step.active 
                        ? 'bg-white/10 border-white/20 shadow-lg backdrop-blur-md' 
                        : 'bg-black/20 border-transparent text-gray-500'
                    }`}
                  >
                    <div className={`h-8 w-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${
                      step.active ? 'bg-white text-black' : step.done ? 'bg-purple-500/20 text-purple-400' : 'bg-white/5 text-gray-400'
                    }`}>
                      {step.done && !step.active ? <CheckCircle2 className="h-5 w-5" /> : step.id}
                    </div>
                    <span className={`font-medium ${step.active ? 'text-white' : 'text-gray-400'}`}>
                      {step.title}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative z-10 flex items-center gap-2 text-sm text-gray-500 font-medium">
              <Sparkles className="h-4 w-4 text-purple-400" />
              Premium Access Included
            </div>
          </div>
        </div>

        {}
        <div className="flex-1 p-8 sm:p-12 lg:p-16 flex flex-col justify-center relative">
          
          <div className="max-w-md w-full mx-auto">
            {}
            <div className="md:hidden flex items-center gap-3 mb-10">
              <div className="flex items-center gap-2 text-white">
                 <KeyRound className="w-8 h-8 -rotate-45 text-white drop-shadow-[0_0_8px_rgba(255,255,255,0.8)]" />
              </div>
              <span className="text-2xl font-bold tracking-tight text-white">Linkey</span>
            </div>

            <div className="mb-10 text-center md:text-left">
              <h2 className="text-3xl font-bold text-white mb-2">
                {isRegistering ? 'Sign Up Account' : 'Sign In'}
              </h2>
              <p className="text-gray-400 text-sm">
                {isRegistering 
                  ? 'Enter your personal data to create your account.'
                  : 'Enter your credentials to access your account.'}
              </p>
            </div>

            {}
            <div className="grid grid-cols-2 gap-4 mb-8">
               <div className="flex justify-center items-center py-2 px-4 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl transition-all h-[46px] overflow-hidden">
                 <LoginButton 
                    botUsername={process.env.REACT_APP_TELEGRAM_BOT_NAME || "OfficialLinkeyBot"}
                    buttonSize="large"
                    cornerRadius={12}
                    showAvatar={false}
                    onAuthCallback={(user) => {
                       axios.post(`${API_URL}/auth/telegram-widget`, user)
                         .then(response => {
                           login(response.data.token, response.data.user);
                           navigate('/dashboard');
                         })
                         .catch(error => {
                           setError('Telegram Login Failed');
                         });
                    }} 
                 />
               </div>
               <button 
                  type="button"
                  onClick={() => {
                      setIsRegistering(false);
                      setShowOtpInput(false);
                      setMessage('');
                      setError('');
                      
                      document.getElementById('email')?.focus();
                  }}
                  className="flex justify-center items-center gap-2 py-3 px-4 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl transition-all text-sm font-medium h-[46px]"
               >
                 <Mail className="h-4 w-4 text-white" />
                 Email
               </button>
            </div>

            <div className="relative mb-8">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-white/10"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-[#121212] text-gray-500 text-xs uppercase tracking-wider">Or continue with</span>
              </div>
            </div>

            <AnimatePresence>
              {error && (
                <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="mb-6 text-red-400 text-sm bg-red-500/10 border border-red-500/20 p-4 rounded-xl flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 shrink-0" /> {error}
                </motion.div>
              )}
              {message && (
                <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="mb-6 text-green-400 text-sm bg-green-500/10 border border-green-500/20 p-4 rounded-xl flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 shrink-0" /> {message}
                </motion.div>
              )}
            </AnimatePresence>

            <form onSubmit={handleSubmit} className="space-y-5">
              {isRegistering && !showOtpInput && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-400 mb-1.5">Full Name</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <UserIcon className="h-5 w-5 text-gray-500" />
                      </div>
                      <input
                        id="name"
                        type="text"
                        required={isRegistering && !showOtpInput}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="block w-full pl-12 pr-4 py-3.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:ring-2 focus:ring-white/20 focus:border-white/30 transition-all sm:text-sm"
                        placeholder="e.g. John Doe"
                      />
                    </div>
                  </motion.div>
              )}

              <div className={showOtpInput ? 'hidden' : ''}>
                <label htmlFor="email" className="block text-sm font-medium text-gray-400 mb-1.5">Email Address</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-500" />
                  </div>
                  <input
                    id="email"
                    type="email"
                    required={!showOtpInput}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="block w-full pl-12 pr-4 py-3.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:ring-2 focus:ring-white/20 focus:border-white/30 transition-all sm:text-sm"
                    placeholder="e.g. john@example.com"
                  />
                </div>
              </div>

              <div className={showOtpInput ? 'hidden' : ''}>
                <div className="flex justify-between items-center mb-1.5">
                  <label htmlFor="password" className="block text-sm font-medium text-gray-400">Password</label>
                  {!isRegistering && (
                    <Link to="/forgot-password" className="text-xs font-medium text-purple-400 hover:text-purple-300 transition-colors">
                      Forgot Password?
                    </Link>
                  )}
                </div>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <KeyRound className="h-5 w-5 text-gray-500" />
                  </div>
                  <input
                    id="password"
                    type="password"
                    required={!showOtpInput}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="block w-full pl-12 pr-4 py-3.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:ring-2 focus:ring-white/20 focus:border-white/30 transition-all sm:text-sm"
                    placeholder="••••••••"
                  />
                </div>
                {isRegistering && (
                  <p className="text-xs text-gray-500 mt-2">Must be at least 8 characters.</p>
                )}
              </div>

              {showOtpInput && (
                  <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
                    <label htmlFor="otp" className="block text-sm font-medium text-gray-400 mb-1.5 text-center">
                      Verification Code
                    </label>
                    <p className="text-xs text-gray-500 text-center mb-4">We've sent a 6-digit code to your email.</p>
                    <div className="relative">
                      <input
                        id="otp"
                        type="text"
                        required={showOtpInput}
                        value={otp}
                        onChange={(e) => setOtp(e.target.value)}
                        className="block w-full px-4 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all text-center tracking-[0.5em] text-2xl font-bold"
                        placeholder="123456"
                        maxLength={6}
                      />
                    </div>
                  </motion.div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center items-center gap-2 py-4 px-4 border border-transparent rounded-xl text-sm font-bold text-black bg-white hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-[#121212] focus:ring-white disabled:opacity-50 transition-all mt-8"
              >
                {loading ? <Loader2 className="animate-spin h-5 w-5" /> : (isRegistering ? (showOtpInput ? 'Verify & Create Account' : 'Sign Up') : 'Sign In')}
                {!loading && <ArrowRight className="h-4 w-4" />}
              </button>
            </form>

            <div className="text-center mt-8">
              <button
                type="button"
                onClick={() => {
                    setIsRegistering(!isRegistering);
                    setShowOtpInput(false);
                    setMessage('');
                    setError('');
                }}
                className="text-sm text-gray-400 hover:text-white transition-colors"
              >
                {isRegistering ? (
                  <span>Already have an account? <span className="text-white font-bold">Log in</span></span>
                ) : (
                  <span>Don't have an account? <span className="text-white font-bold">Sign up</span></span>
                )}
              </button>
            </div>
            
          </div>
        </div>

      </div>
    </div>
  );
};

export default Login;